import { Client, Databases, ID, Permission, Role, Query } from 'appwrite';
import { generateReferralCode } from '../config/app.js';

// Initialize Appwrite client
const client = new Client();

// Set the endpoint and project ID from environment variables
client
    .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
    .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

// Get references to Appwrite services
export const databases = new Databases(client);

// Database and collection IDs from environment variables
export const USERS_DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
export const USERS_COLLECTION_ID = process.env.REACT_APP_APPWRITE_COLLECTION_ID;

// Global flag to prevent concurrent registrations
let registrationInProgress = new Set();

// Rate limiting for referral abuse prevention
const referralAttempts = new Map();

// Helper function to check if user is trying to abuse referrals
const checkReferralRateLimit = (userId) => {
    const now = Date.now();
    const userAttempts = referralAttempts.get(userId) || [];
    
    // Remove attempts older than 1 hour
    const recentAttempts = userAttempts.filter(time => now - time < 60 * 60 * 1000);
    
    // Allow max 3 referral attempts per hour
    if (recentAttempts.length >= 3) {
        return false;
    }
    
    // Add current attempt
    recentAttempts.push(now);
    referralAttempts.set(userId, recentAttempts);
    
    return true;
};

// Create user document in the database
export const createUserDocument = async (user, referredBy = null) => {
    try {
        if (!user || !user.id) {
            console.error('Cannot create document: user or user.id is missing', user);
            return null;
        }

        const userId = user.id.toString();

        // Check if registration is already in progress for this user
        if (registrationInProgress.has(userId)) {
            return null;
        }

        // Mark registration as in progress
        registrationInProgress.add(userId);

        try {
            // First, check if user already exists
            let exists = false;
            try {
                exists = await checkUserExists(user.id);
            } catch (checkError) {
                console.error('Error checking user existence:', checkError);
                // If we can't verify existence, we should not create a new document
                return null;
            }

            if (exists) {
                return true;
            }

            // Generate unique referral code for this user
            const referralCode = generateReferralCode(userId);

            // Validate referrer exists before processing referral bonus
            let validReferrer = null;
            if (referredBy) {
                // Prevent self-referral
                if (referredBy === userId) {
                    // Self-referral ignored
                } else if (!checkReferralRateLimit(userId)) {
                    // Rate limit exceeded
                } else {
                    try {
                        const referrerExists = await checkUserExists(referredBy);
                        if (referrerExists) {
                            validReferrer = referredBy;
                        }
                    } catch (error) {
                        console.error('Error validating referrer:', error);
                        // If we can't validate, don't give bonus
                    }
                }
            }

            // Initialize user with default mining values and referral data
            const documentData = {
                telegram_id: userId,
                username: user.username || '',
                doge_balance: 0,
                mined_amount: 0, // Start fresh for all users
                last_claim_time: null,
                last_mining_time: new Date().toISOString(),
                miner_power: 1.0, // Default 1 DOGE per second
                referral_code: referralCode,
                referred_by: validReferrer, // Only set if referrer is valid
                pxl_balance: validReferrer ? 1000 : 0 // Only give bonus if referrer is valid
            };

            const document = await databases.createDocument(
                USERS_DATABASE_ID,
                USERS_COLLECTION_ID,
                ID.unique(),
                documentData,
                [Permission.read(Role.any()), Permission.write(Role.any())]
            );

            // If user was referred by a valid referrer, give bonus to the referrer as well
            if (validReferrer) {
                try {
                    await giveReferralBonus(validReferrer);
                } catch (bonusError) {
                    console.error('Error giving referral bonus:', bonusError);
                    // Don't fail the registration if bonus fails
                }
            }

            return document;
        } finally {
            // Always remove from in-progress set
            registrationInProgress.delete(userId);
        }
    } catch (error) {
        console.error('Error creating user document:', error);
        return null;
    }
};


// Check if a user already exists
export const checkUserExists = async (userId) => {
    if (!userId) {
        console.error('Cannot check user: userId is missing');
        return false;
    }
    
    try {        
        // Convert userId to string to ensure proper querying
        const userIdString = userId.toString();
        
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        // Check if we got any documents back
        const exists = response && response.documents && response.documents.length > 0;
        
        return exists;
    } catch (error) {
        // For CORS or other errors, we throw the error to prevent creating duplicates
        console.error('Error checking if user exists:', error);
        throw error; // This will prevent document creation in case of errors
    }
};

// Claim mined DOGE coins
export const claimMinedDoge = async (userId, minedAmount) => {
    if (!userId || !minedAmount || minedAmount < 100) {
        console.error('Invalid claim request: userId or minedAmount insufficient');
        return { success: false, error: 'Minimum 100 DOGE required to claim' };
    }
    
    const userIdString = userId.toString();
    
    try {
        // First, get the current user document
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        if (!response || !response.documents || response.documents.length === 0) {
            return { success: false, error: 'User not found' };
        }
          const userDoc = response.documents[0];
        const currentBalance = userDoc.doge_balance || 0;
        
        // Calculate new balance and reset mined amount
        const newBalance = currentBalance + minedAmount;
        const currentTime = new Date().toISOString();
          // Update the user document
        await databases.updateDocument(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            userDoc.$id,
            {
                doge_balance: newBalance,
                mined_amount: 0, // Reset mined amount after claiming
                last_claim_time: currentTime
            }
        );
        
        return {
            success: true,
            claimedAmount: minedAmount,
            newBalance: newBalance,
            claimTime: currentTime
        };
        
    } catch (error) {
        console.error('Error claiming DOGE:', error);
        return { success: false, error: error.message || 'Failed to process claim' };
    }
};

// Get user balance and mining data
export const getUserData = async (userId) => {
    if (!userId) {
        console.error('Cannot get user data: userId is missing');
        return null;
    }
    
    try {
        const userIdString = userId.toString();
        
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        if (!response || !response.documents || response.documents.length === 0) {
            return null;
        }
        
        const userDoc = response.documents[0];
          return {
            balance: userDoc.doge_balance || 0,
            minedAmount: userDoc.mined_amount || 0,
            lastClaimTime: userDoc.last_claim_time || null,
            lastMiningTime: userDoc.last_mining_time || null,
            minerPower: userDoc.miner_power || 1.0,
            username: userDoc.username || '',
            referral_code: userDoc.referral_code || null,
            referred_by: userDoc.referred_by || null,
            pxl_balance: userDoc.pxl_balance || 0
        };
        
    } catch (error) {
        console.error('Error getting user data:', error);        return null;
    }
};

// Calculate and update offline mining rewards
export const calculateOfflineMining = async (userId) => {
    if (!userId) {
        console.error('Cannot calculate offline mining: userId is missing');
        return null;
    }
    
    try {
        const userIdString = userId.toString();
        
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        if (!response || !response.documents || response.documents.length === 0) {
            return null;
        }
          const userDoc = response.documents[0];
        const currentTime = new Date();
        const lastMiningTime = userDoc.last_mining_time ? new Date(userDoc.last_mining_time) : currentTime;
        const minerPower = userDoc.miner_power || 1.0;
        const currentBalance = userDoc.doge_balance || 0;
        const currentMined = userDoc.mined_amount || 0;
        
        // Calculate elapsed time in seconds
        const elapsedSeconds = Math.max(0, (currentTime - lastMiningTime) / 1000);
        
        // Calculate mined amount (coins per second * elapsed seconds)
        const minedWhileAway = elapsedSeconds * minerPower;
        
        // Update the user document with new values
        await databases.updateDocument(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            userDoc.$id,
            {
                mined_amount: currentMined + minedWhileAway,
                last_mining_time: currentTime.toISOString()
            }
        );
        
        return {
            elapsedSeconds,
            minedWhileAway,
            newMinedAmount: currentMined + minedWhileAway,
            minerPower,
            balance: currentBalance
        };
        
    } catch (error) {
        console.error('Error calculating offline mining:', error);
        return null;
    }
};

// Update user's miner power (for upgrades)
export const updateMinerPower = async (userId, newPower) => {
    if (!userId || newPower <= 0) {
        console.error('Invalid miner power update request');
        return false;
    }
    
    try {
        const userIdString = userId.toString();
        
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        if (!response || !response.documents || response.documents.length === 0) {
            return false;
        }
        
        const userDoc = response.documents[0];
        
        await databases.updateDocument(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            userDoc.$id,
            {
                miner_power: newPower
            }
        );
        
        return true;
        
    } catch (error) {
        console.error('Error updating miner power:', error);
        return false;
    }
};

// Update last mining time when user becomes inactive/leaves app
export const updateLastMiningTime = async (userId) => {
    if (!userId) {
        console.error('Cannot update last mining time: userId is missing');
        return false;
    }
    
    try {
        const userIdString = userId.toString();
        
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        if (!response || !response.documents || response.documents.length === 0) {
            return false;
        }
        
        const userDoc = response.documents[0];
        
        await databases.updateDocument(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            userDoc.$id,
            {
                last_mining_time: new Date().toISOString()
            }
        );
        
        return true;
        
    } catch (error) {
        console.error('Error updating last mining time:', error);
        return false;
    }
};

// Give referral bonus to the referrer
export const giveReferralBonus = async (referrerId) => {
    if (!referrerId) {
        console.error('Cannot give bonus: referrerId is missing');
        return false;
    }
    
    try {
        const referrerIdString = referrerId.toString();
        
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', referrerIdString)]
        );
        
        if (!response || !response.documents || response.documents.length === 0) {
            console.error('Referrer not found:', referrerId);
            return false;
        }
        
        const referrerDoc = response.documents[0];
        const currentPxlBalance = referrerDoc.pxl_balance || 0;
        const newPxlBalance = currentPxlBalance + 1000; // Add 1000 PXL bonus
        
        await databases.updateDocument(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            referrerDoc.$id,
            {
                pxl_balance: newPxlBalance
            }
        );
        
        return true;
        
    } catch (error) {
        console.error('Error giving referral bonus:', error);
        return false;
    }
};

// Get user's referral statistics
export const getReferralStats = async (userId) => {
    if (!userId) {
        console.error('Cannot get referral stats: userId is missing');
        return null;
    }
    
    try {
        const userIdString = userId.toString();
        
        // Get user's referral code first
        const userResponse = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('telegram_id', userIdString)]
        );
        
        if (!userResponse || !userResponse.documents || userResponse.documents.length === 0) {
            return null;
        }
        
        const userDoc = userResponse.documents[0];
        const referralCode = userDoc.referral_code;
        
        // Count how many users were referred by this user
        const referredUsersResponse = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('referred_by', userIdString)]
        );
        
        const referredCount = referredUsersResponse ? referredUsersResponse.documents.length : 0;
        
        return {
            referralCode,
            referredCount,
            pxlBalance: userDoc.pxl_balance || 0
        };
        
    } catch (error) {
        console.error('Error getting referral stats:', error);
        return null;
    }
};

// Get user by referral code
export const getUserByReferralCode = async (referralCode) => {
    if (!referralCode) {
        return null;
    }
    
    try {
        const response = await databases.listDocuments(
            USERS_DATABASE_ID,
            USERS_COLLECTION_ID,
            [Query.equal('referral_code', referralCode)]
        );
        
        if (response && response.documents && response.documents.length > 0) {
            return response.documents[0];
        }
        
        return null;
        
    } catch (error) {
        console.error('Error finding user by referral code:', error);
        return null;
    }
};

